import { ReactElement } from 'react';

import { environment, SiteMode } from '../../../api';
import { IAuthStore } from '../../../auth';

import ClientIdAndSecret from './tenant-apis/getting-started/client-id-and-secret.md';
import CreateAUser from './tenant-apis/getting-started/create-a-user.md';
import RegisterAnApp from './tenant-apis/getting-started/register-an-app.md';
import Environments from './tenant-apis/getting-started/environments.md';
import FirstApiCall from './tenant-apis/getting-started/first-api-call.md';
import GettingAccess from './tenant-apis/getting-started/getting-access.md';
import GoingLive from './tenant-apis/getting-started/going-live.md';
import OAuth20 from './tenant-apis/getting-started/oauth20.md';
import TransactionalAndExportApis from './tenant-apis/getting-started/transactional-and-export-apis.md';
import Webhooks from './tenant-apis/getting-started/webhooks.md';
import Welcome from './tenant-apis/getting-started/welcome.md';

import AccessAndEnvironments from './tenant-apis/faqs/access-and-environments.md';
import AppKeysAndClientKeys from './tenant-apis/faqs/app-keys-and-client-keys.md';
import Customers from './tenant-apis/faqs/customers.md';
import Developers from './tenant-apis/faqs/developers.md';
import Marketplace from './tenant-apis/faqs/marketplace.md';
import Support from './tenant-apis/faqs/support.md';
import ThirdPartyAppMigration from './tenant-apis/faqs/third-party-app-migration.md';
import V2Webhooks from './tenant-apis/faqs/v2-webhooks.md';

export interface Topic {
    id: string;
    title: string;
    subtopics: Subtopic[];
}

export interface Subtopic {
    id: string;
    title: string;
    content: ReactElement;
    filter?: (authStore: IAuthStore) => boolean; // temporarily added for the V2 Webhooks beta
}

const partnerApisTopics: Topic[] = [];

const tenantApisTopics: Topic[] = [
    {
        id: 'getting-started',
        title: 'Getting Started',
        subtopics: [
            { id: 'welcome', title: 'Welcome', content: Welcome },
            { id: 'getting-access', title: 'Getting Access', content: GettingAccess },
            { id: 'create-a-user', title: 'Create a User', content: CreateAUser },
            { id: 'register-an-app', title: 'Register an App', content: RegisterAnApp },
            { id: 'client-id-secret', title: 'Client ID & Secret', content: ClientIdAndSecret },
            { id: 'environments', title: 'Environments', content: Environments },
            { id: 'first-api-call', title: 'First API Call', content: FirstApiCall },
            { id: 'going-live', title: 'Going Live', content: GoingLive },
            { id: 'oauth20', title: 'OAuth 2.0', content: OAuth20 },
            {
                id: 'transactional-and-export-api',
                title: 'Transactional & Export APIs',
                content: TransactionalAndExportApis,
            },
            {
                id: 'webhooks',
                title: 'V2 Webhooks (Beta)',
                content: Webhooks,
                filter: authStore => authStore.canAccessWebhooks,
            },
        ],
    },
    {
        id: 'faqs',
        title: 'FAQs',
        subtopics: [
            {
                id: 'access-and-environments',
                title: 'Access & Environments',
                content: AccessAndEnvironments,
            },
            {
                id: 'app-keys-and-client-keys',
                title: 'APIs, App Keys & Client Keys',
                content: AppKeysAndClientKeys,
            },
            { id: 'customers', title: 'Customers Engaging with Third Parties', content: Customers },
            { id: 'developers', title: 'Developers', content: Developers },
            {
                id: 'third-party-app-migration',
                title: 'Third-Party App Migration',
                content: ThirdPartyAppMigration,
            },
            { id: 'support', title: 'Support', content: Support },
            { id: 'marketplace', title: 'Marketplace', content: Marketplace },
            {
                id: 'v2-webhooks',
                title: 'V2 Webhooks',
                content: V2Webhooks,
                filter: authStore => authStore.canAccessWebhooks,
            },
        ],
    },
];

export const topics =
    environment.site.mode === SiteMode.PartnerApis ? partnerApisTopics : tenantApisTopics;
